/**
 * The active locale, held apart from the engine.
 *
 * Its own module so that `services/api.js` can read the reader's language for
 * the `X-ISG-Locale` header WITHOUT importing `i18n/index.js` — which itself
 * imports the API client (to persist a choice), and a circular import between
 * the two would work only for as long as nobody touched either at module scope.
 *
 * Only `i18n/index.js` writes it.
 */
import { DEFAULT_LOCALE } from './locales.js';
import chromeEnGB from './locales/en-GB.json';

let active = DEFAULT_LOCALE;

/** The active locale code. Always a supported code; en-GB until an app adopts i18n. */
export function getActiveLocale() {
  return active;
}

/** @internal — called by the engine when the locale changes. */
export function setActiveLocale(code) {
  if (code) active = code;
}

// ── Translation, for the API client ─────────────────────────────────────
//
// `services/api.js` shows a handful of messages a person reads (session
// expired, access restricted, upload failures). It cannot import `t` from
// `i18n/index.js` for the same reason it reads the locale from here — the
// engine imports the API client. So the engine REGISTERS its `t` here, and
// until it has (a module graph that loads the client first), lookups fall
// back to the bundled en-GB chrome catalogue: correct English, never a raw key.

let translator = null;

/** @internal — called once by `i18n/index.js` with its `t`. */
export function setTranslator(fn) {
  if (typeof fn === 'function') translator = fn;
}

/** `t()` for modules that must not import the engine. Same contract as `t`. */
export function translate(key, params = {}) {
  if (translator) return translator(key, params);
  let node = chromeEnGB;
  for (const part of key.split('.')) {
    node = node && typeof node === 'object' ? node[part] : undefined;
  }
  if (typeof node !== 'string') return key;
  return node.replace(/\{(\w+)\}/g, (m, name) => (params[name] === undefined ? m : String(params[name])));
}
